import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import DoctorNavbar from '../components/DoctorNavbar';
import DOMPurify from 'dompurify';
import { toast } from 'react-toastify';
import PostCommentModal from '../components/PostCommentModal';

function DoctorPosts() {
  const navigate = useNavigate();
  const [allPosts, setAllPosts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [doctorId, setDoctorId] = useState(null);
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [savedPosts, setSavedPosts] = useState(new Set());
  const [showSavedOnly, setShowSavedOnly] = useState(false);
  const [expandedPost, setExpandedPost] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [commentModalPost, setCommentModalPost] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);
  const [showOnlyMyPosts, setShowOnlyMyPosts] = useState(false);

  useEffect(() => {
    checkSession();
  }, []);

  useEffect(() => {
    if (doctorId) {
      fetchPosts();
      // Polling removed as per user request
    }
  }, [doctorId]);

  useEffect(() => {
    if (allPosts.length > 0) {
      if (showOnlyMyPosts) {
        const myPosts = allPosts.filter(post => isPostOwner(post));
        const sortedMyPosts = myPosts.sort((a, b) =>
          new Date(b.created_at) - new Date(a.created_at)
        );
        setPosts(sortedMyPosts);
      } else {
        setPosts(allPosts);
      }
    }
  }, [showOnlyMyPosts, allPosts]);

  const checkSession = async () => {
    try {
      const res = await api.get('doctor/session-check/');
      if (res.data.authenticated) {
        setDoctorId(res.data.doctor_id);
      } else {
        navigate('/doctor/login');
      }
    } catch (err) {
      console.error("Session check failed:", err);
      navigate('/doctor/login');
    }
  };

  const fetchPosts = async () => {
    try {
      const res = await api.get('social/posts/', {
        params: { doctor_id: doctorId }
      });
      const sortedPosts = res.data.sort((a, b) =>
        new Date(b.created_at) - new Date(a.created_at)
      );
      setAllPosts(sortedPosts);

      const postsToUse = showOnlyMyPosts
        ? sortedPosts.filter(post => isPostOwner(post))
        : sortedPosts;
      setPosts(postsToUse);

      const liked = new Set();
      const saved = new Set();
      sortedPosts.forEach(post => {
        if (post.is_liked) liked.add(post.id);
        if (post.is_saved) saved.add(post.id);
      });
      setLikedPosts(liked);
      setSavedPosts(saved);
      return sortedPosts;
    } catch (err) {
      console.error("Error fetching posts:", err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const syncCommentModalPost = (sortedPosts) => {
    if (!commentModalPost || !sortedPosts) return;
    const updated = sortedPosts.find((p) => p.id === commentModalPost.id);
    if (updated) setCommentModalPost(updated);
  };

  const openCommentModal = (post) => {
    setCommentText('');
    setCommentModalPost(post);
  };

  const closeCommentModal = () => {
    setCommentModalPost(null);
    setCommentText('');
  };

  const isPostOwner = (post) => {
    if (!doctorId || !post) return false;
    return (
      (post.doctor && (post.doctor === parseInt(doctorId) || post.doctor === doctorId)) ||
      (post.doctor_id && (post.doctor_id === parseInt(doctorId) || post.doctor_id === doctorId))
    );
  };

  const applyFilter = (postsToFilter) => {
    if (!postsToFilter || postsToFilter.length === 0) return;

    if (showOnlyMyPosts) {
      const myPosts = postsToFilter.filter(post => isPostOwner(post));
      const sortedMyPosts = myPosts.sort((a, b) =>
        new Date(b.created_at) - new Date(a.created_at)
      );
      setPosts(sortedMyPosts);
    } else {
      setPosts(postsToFilter);
    }
  };

  const handleShowAllPosts = () => {
    setShowOnlyMyPosts(false);
  };

  const handleShowMyPosts = () => {
    setShowOnlyMyPosts(true);
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    if (image.startsWith('http://') || image.startsWith('https://')) {
      return image;
    }
    if (image.startsWith('/media/')) {
      return `${apiUrl}${image}`;
    }
    return `${apiUrl}/media/${image}`;
  };

  const handleSave = async (postId) => {
    const isSaved = savedPosts.has(postId);

    if (isSaved) {
      setSavedPosts(prev => {
        const next = new Set(prev);
        next.delete(postId);
        return next;
      });
    } else {
      setSavedPosts(prev => new Set(prev).add(postId));
    }

    try {
      await api.post(`social/posts/${postId}/save/`, { doctor_id: doctorId });
      const sorted = await fetchPosts();
      syncCommentModalPost(sorted);
      toast.success(isSaved ? 'Post removed from saved' : 'Post saved!', {
        position: 'bottom-right',
        autoClose: 2000,
      });
    } catch (err) {
      console.error('Error saving post:', err);
      if (isSaved) {
        setSavedPosts(prev => new Set(prev).add(postId));
      } else {
        setSavedPosts(prev => {
          const next = new Set(prev);
          next.delete(postId);
          return next;
        });
      }
      toast.error('Failed to save post. Please try again.', {
        position: 'bottom-right',
        autoClose: 3000,
      });
    }
  };

  const handleLike = async (postId) => {
    const isLiked = likedPosts.has(postId);

    // Optimistic update
    if (isLiked) {
      setLikedPosts(prev => {
        const newSet = new Set(prev);
        newSet.delete(postId);
        return newSet;
      });
      setPosts(prev => prev.map(p =>
        p.id === postId ? { ...p, likes_count: Math.max(0, p.likes_count - 1) } : p
      ));
    } else {
      setLikedPosts(prev => new Set(prev).add(postId));
      setPosts(prev => prev.map(p =>
        p.id === postId ? { ...p, likes_count: (p.likes_count || 0) + 1 } : p
      ));
    }

    try {
      await api.post(`social/posts/${postId}/like/`, {
        doctor_id: doctorId
      });
      const sorted = await fetchPosts();
      syncCommentModalPost(sorted);
    } catch (err) {
      console.error("Error liking post:", err);
      // Revert on error
      if (isLiked) {
        setLikedPosts(prev => new Set(prev).add(postId));
        setPosts(prev => prev.map(p =>
          p.id === postId ? { ...p, likes_count: (p.likes_count || 0) + 1 } : p
        ));
      } else {
        setLikedPosts(prev => {
          const newSet = new Set(prev);
          newSet.delete(postId);
          return newSet;
        });
        setPosts(prev => prev.map(p =>
          p.id === postId ? { ...p, likes_count: Math.max(0, p.likes_count - 1) } : p
        ));
      }
    }
  };

  const handleComment = async () => {
    if (!commentModalPost) return;
    const comment = commentText?.trim();
    if (!comment) return;

    try {
      await api.post(`social/posts/${commentModalPost.id}/add_comment/`, {
        doctor_id: doctorId,
        content: comment
      });
      setCommentText('');
      const sorted = await fetchPosts();
      syncCommentModalPost(sorted);
    } catch (err) {
      console.error("Error adding comment:", err);
      toast.error("Failed to add comment. Please try again.", {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
  };

  const handleShare = async (postId) => {
    try {
      const res = await api.get(`social/posts/${postId}/generate_share_link/`);
      if (res.data.share_link) {
        navigator.clipboard.writeText(res.data.share_link);
        toast.success("Share link copied to clipboard!", {
          position: "bottom-right",
          autoClose: 3000,
        });
        fetchPosts();
      }
    } catch (err) {
      console.error("Error generating share link:", err);
      toast.error("Failed to generate share link. Please try again.", {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
  };

  const handleDeleteClick = (postId) => {
    setPostToDelete(postId);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!postToDelete) return;

    try {
      // Send doctor_id as query parameter (more reliable for DELETE requests)
      await api.delete(`social/posts/${postToDelete}/?doctor_id=${doctorId}`);
      toast.success("Post deleted successfully!", {
        position: "bottom-right",
        autoClose: 3000,
      });
      fetchPosts();
    } catch (err) {
      console.error("Error deleting post:", err);
      toast.error(err.response?.data?.error || "Failed to delete post. Please try again.", {
        position: "bottom-right",
        autoClose: 3000,
      });
    } finally {
      setShowDeleteConfirm(false);
      setPostToDelete(null);
    }
  };

  const toggleExpand = (postId) => {
    setExpandedPost(expandedPost === postId ? null : postId);
  };

  if (loading) {
    return (
      <div className="bg-light min-vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-light min-vh-100">
      <DoctorNavbar />
      <div className="container py-4">

        {/* HEADER SECTION */}
        <div className="row mb-4 align-items-center">
          <div className="col-12 col-md-8">
            <h2 className="fw-bold text-dark mb-0 fs-4 fs-md-3">
              <i className="bi bi-newspaper me-2 text-primary"></i>
              {showOnlyMyPosts ? 'Your Posts' : 'All Posts'}
            </h2>
            <p className="text-muted mb-0 mb-md-2">
              {showSavedOnly
                ? 'Posts you saved for later.'
                : showOnlyMyPosts
                  ? 'Viewing only your health posts.'
                  : 'View and manage all your health posts.'}
            </p>
          </div>
          <div className="col-12 col-lg-auto mt-2 mt-lg-0">
            <div className="d-flex flex-wrap gap-2">
              <button
                type="button"
                className={`btn btn-sm rounded-pill px-3 ${!showSavedOnly && !showOnlyMyPosts ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => { setShowSavedOnly(false); handleShowAllPosts(); }}
              >
                All Posts
              </button>
              <button
                type="button"
                className={`btn btn-sm rounded-pill px-3 ${showSavedOnly ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => { setShowSavedOnly(true); setShowOnlyMyPosts(false); }}
              >
                <i className="bi bi-bookmark-fill me-1"></i>Saved
              </button>
            </div>
          </div>
          {/* <div className="col-auto">
            <button className="btn btn-primary shadow-sm rounded-pill px-4" onClick={() => navigate('/doctor/create-post')}>
              <i className="bi bi-plus-circle me-2"></i>Create New Post
            </button>
          </div> */}
        </div>

        {/* STATS SUMMARY */}
        {allPosts.length > 0 && (
          <div className="row g-3 mb-4">
            <div className="col-md-3">
              <div
                className="card border-0 shadow-hover rounded-4 text-center p-3 bg-soft-primary"
                style={{ cursor: 'pointer' }}
                onClick={handleShowAllPosts}
              >
                <h4 className="fw-bold text-primary mb-0">{allPosts.length}</h4>
                <small className="text-muted text-uppercase fw-bold">Total Posts</small>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card border-0 shadow-sm rounded-4 text-center p-3 bg-success bg-opacity-10">
                <h4 className="fw-bold text-success mb-0">
                  {allPosts.filter(p => isPostOwner(p)).reduce((sum, p) => sum + (p.likes_count || 0), 0)}
                </h4>
                <small className="text-muted text-uppercase fw-bold">Your Likes</small>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card border-0 shadow-sm rounded-4 text-center p-3 bg-info bg-opacity-10">
                <h4 className="fw-bold text-info mb-0">
                  {allPosts.filter(p => isPostOwner(p)).reduce((sum, p) => sum + (p.comments?.length || 0), 0)}
                </h4>
                <small className="text-muted text-uppercase fw-bold">Your Comments</small>
              </div>
            </div>
            <div className="col-md-3">
              <div
                className="card border-0 shadow-sm rounded-4 text-center p-3 bg-warning bg-opacity-10"
                style={{ cursor: 'pointer' }}
                onClick={handleShowMyPosts}
              >
                <h4 className="fw-bold text-warning mb-0">
                  {allPosts.filter(p => isPostOwner(p)).length}
                </h4>
                <small className="text-muted text-uppercase fw-bold">Your Posts</small>
              </div>
            </div>
          </div>
        )}

        {/* POSTS FEED */}
        {(() => {
          const visiblePosts = showSavedOnly
            ? posts.filter((p) => savedPosts.has(p.id))
            : posts;
          return visiblePosts.length === 0 ? (
          <div className="text-center py-5">
            <div className="card border-0 shadow-sm">
              <div className="card-body py-5">
                <i className="bi bi-inbox fs-1 text-muted mb-3 d-block"></i>
                <h5 className="fw-bold">{showSavedOnly ? 'No saved posts' : 'No posts available'}</h5>
                <p className="text-muted">
                  {showSavedOnly
                    ? 'Tap the bookmark icon on a post to save it here.'
                    : showOnlyMyPosts
                      ? "You haven't created any posts yet. Create your first health post to get started!"
                      : 'Create your first health post to get started!'}
                </p>
                {showOnlyMyPosts && allPosts.length > 0 && (
                  <button className="btn btn-outline-primary mt-2 me-2" onClick={handleShowAllPosts}>
                    <i className="bi bi-arrow-left me-2"></i>View All Posts
                  </button>
                )}
                <button className="btn btn-primary mt-3" onClick={() => navigate('/doctor/create-post')}>
                  <i className="bi bi-plus-circle me-2"></i>Create Post
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="row g-4">
            {visiblePosts.map((post) => {
              const isLiked = likedPosts.has(post.id);
              const isSaved = savedPosts.has(post.id);
              const isExpanded = expandedPost === post.id;
              const content = post.large_content || post.short_content || '';
              // Strip HTML tags to get plain text for preview
              const plainText = content.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
              const shouldTruncate = plainText.length > 200 && !isExpanded;
              const displayContent = shouldTruncate
                ? DOMPurify.sanitize(content)
                : DOMPurify.sanitize(content);
              const previewText = shouldTruncate ? plainText.substring(0, 200) + '...' : null;
              // Check if current doctor owns this post
              const isOwner = isPostOwner(post);

              return (
                <div key={post.id} className="col-12 col-lg-6">
                  <div className="card border-0 shadow-sm hover-shadow h-100 rounded-4">
                    <div className="card-body p-4">

                      {/* POST HEADER */}
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center">
                          <div className="bg-primary bg-opacity-10 text-white rounded-circle p-2 me-3" style={{ width: '45px', height: '45px' }}>
                            <i className="bi bi-person-badge fs-5 d-flex align-items-center text-white justify-content-center h-100"></i>
                          </div>
                          <div>
                            <h6 className="fw-bold mb-0">{post.doctor_name || 'Doctor'}</h6>
                            <small className="text-muted">
                              <i className="bi bi-clock me-1"></i>
                              {new Date(post.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </small>
                          </div>
                        </div>
                        {/* Three-dot dropdown removed — View/Edit/Delete buttons are at the bottom */}
                      </div>

                      {/* POST TITLE */}
                      {post.title && (
                        <h5 className="fw-bold mb-3 text-dark">{post.title}</h5>
                      )}

                      {/* POST IMAGE */}
                      {post.image && (
                        <div className="mb-3 position-relative rounded-3 overflow-hidden" style={{ maxHeight: '300px' }}>
                          <img
                            src={getImageUrl(post.image)}
                            alt={post.title || post.doctor_name || 'Post image'}
                            className="img-fluid w-100"
                            style={{
                              height: '300px',
                              objectFit: 'cover',
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              const imgUrl = getImageUrl(post.image);
                              if (imgUrl) window.open(imgUrl, '_blank');
                            }}
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/800x400?text=Image+Not+Available';
                              e.target.style.opacity = '0.5';
                            }}
                            loading="lazy"
                          />
                        </div>
                      )}

                      {/* POST CONTENT */}
                      {shouldTruncate ? (
                        // Show plain text preview with inline '...'
                        <p
                          className="mb-2 text-muted"
                          style={{ fontSize: '0.9rem', lineHeight: '1.7' }}
                        >
                          {previewText}
                        </p>
                      ) : (
                        // Show full HTML content when expanded or short
                        <div
                          className="mb-2 text-muted"
                          style={{ fontSize: '0.9rem', lineHeight: '1.7' }}
                          dangerouslySetInnerHTML={{ __html: displayContent }}
                        />
                      )}

                      {/* EXPAND/COLLAPSE BUTTON */}
                      {plainText.length > 200 && (
                        <button
                          className="btn btn-link p-0 text-primary text-decoration-none mb-3 fw-medium"
                          onClick={() => toggleExpand(post.id)}
                        >
                          {isExpanded ? (
                            <><i className="bi bi-chevron-up me-1"></i>Show Less</>
                          ) : (
                            <><i className="bi bi-chevron-down me-1"></i>Read More</>
                          )}
                        </button>
                      )}

                      {/* POST ACTIONS */}
                      <div className="d-flex align-items-center justify-content-between border-top pt-3 mt-auto">
                        <div className="d-flex gap-3">
                          <button
                            className="btn btn-link p-0 text-decoration-none d-flex align-items-center"
                            onClick={() => handleLike(post.id)}
                            style={{ border: 'none', background: 'none' }}
                          >
                            <i className={`bi ${isLiked ? 'bi-heart-fill text-danger' : 'bi-heart'} fs-5 me-2`}></i>
                            <span className="small fw-medium">{post.likes_count || 0}</span>
                          </button>

                          <button
                            className="btn btn-link p-0 text-decoration-none d-flex align-items-center"
                            onClick={() => openCommentModal(post)}
                            style={{ border: 'none', background: 'none' }}
                          >
                            <i className="bi bi-chat-dots fs-5 text-primary me-2"></i>
                            <span className="small fw-medium">{post.comments?.length || 0}</span>
                          </button>

{/* <button
  className="btn btn-link p-0 text-decoration-none d-flex align-items-center"
  onClick={() => handleShare(post.id)}
  style={{ border: 'none', background: 'none' }}
>
  <i className="bi bi-share fs-5 text-primary me-2"></i>
  <span className="small fw-medium">Share</span>
</button> */}

                          <button
                            className="btn btn-link p-0 text-decoration-none d-flex align-items-center"
                            onClick={() => handleSave(post.id)}
                            style={{ border: 'none', background: 'none' }}
                            title={isSaved ? 'Unsave' : 'Save'}
                          >
                            <i className={`bi ${isSaved ? 'bi-bookmark-fill text-dark' : 'bi-bookmark'} fs-5`}></i>
                          </button>
                        </div>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                            onClick={() => navigate(`/post/${post.id}`)}
                            title="View Full Post"
                          >
                            <i className="bi bi-eye me-1"></i>View
                          </button>
                          {isOwner && (
                            <>
                              <button
                                className="btn btn-sm btn-outline-primary rounded-pill px-3"
                                onClick={() => navigate(`/post/${post.id}?edit=true`)}
                                title="Edit Post"
                              >
                                <i className="bi bi-pencil me-1"></i>Edit
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger rounded-pill px-3"
                                onClick={() => handleDeleteClick(post.id)}
                                title="Delete Post"
                              >
                                <i className="bi bi-trash me-1"></i>Delete
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
        })()}

      </div>

      <style>{`
        .hover-shadow {
          transition: all 0.3s ease;
        }
        .hover-shadow:hover {
          transform: translateY(-5px);
          box-shadow: 0 0.75rem 2rem rgba(0,0,0,.12) !important;
        }
        .card {
          transition: all 0.3s ease;
        }
        .card:hover {
          border-color: rgba(13, 110, 253, 0.2) !important;
        }
        .card[style*="cursor: pointer"]:hover {
          transform: translateY(-3px);
          box-shadow: 0 0.5rem 1.5rem rgba(0,0,0,.15) !important;
        }
      `}</style>

      <PostCommentModal
        post={commentModalPost}
        isOpen={!!commentModalPost}
        onClose={closeCommentModal}
        commentText={commentText}
        onCommentTextChange={setCommentText}
        onSubmitComment={handleComment}
        onLike={() => commentModalPost && handleLike(commentModalPost.id)}
        // onShare={() => commentModalPost && handleShare(commentModalPost.id)}
        onSave={() => commentModalPost && handleSave(commentModalPost.id)}
        isLiked={commentModalPost ? likedPosts.has(commentModalPost.id) : false}
        isSaved={commentModalPost ? savedPosts.has(commentModalPost.id) : false}
      />

      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteConfirm && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-danger">
                  <i className="bi bi-exclamation-triangle me-2"></i>Confirm Delete
                </h5>
                <button type="button" className="btn-close" onClick={() => {
                  setShowDeleteConfirm(false);
                  setPostToDelete(null);
                }}></button>
              </div>
              <div className="modal-body p-4">
                <p className="mb-0">
                  Are you sure you want to delete this post? This action cannot be undone.
                </p>
              </div>
              <div className="modal-footer border-0">
                <button
                  type="button"
                  className="btn btn-outline-secondary rounded-3"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setPostToDelete(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger rounded-3 fw-bold"
                  onClick={handleDeleteConfirm}
                >
                  <i className="bi bi-trash me-2"></i>Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DoctorPosts;
